import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAmplitudeExperiment } from '../utils/ampli';
import config from 'config';
import { selectUser } from '../reducers/user.selectors';
import { useSelector, useStore } from 'react-redux';
import { useTriggeredHint } from '../utils/useTriggeredHint';
import useRouter from '../utils/useRouter';
import services from '../services';
import { onboardingHints, routes } from '../constants';
import { StoreType } from '../reducers';
import { getMindItem } from '../reducers/mindItems.selectors';
import {
  getAiGeneratedImagesRemainingLimit,
  getCurrentConversation,
  getCurrentConversationId,
  getGeneratedPodcastId,
  getGeneratingPodcastIdForCurrentConversation,
  getLastGeneratedImageUrl,
  getLastGeneratedArtifactId,
  getPromptsAvailableAgainAt,
  getPromptsRemainingLimit,
  getTemporaryAttachments,
  getTemporaryConversation,
} from '../reducers/aiAssistant.selectors';
import {
  ABORT_REASON,
  createNewConversation as createNewConversationAction,
  streamConversationResponse as streamConversationResponseAction,
  addNewMessage as addNewMessageAction,
  removeLastAssistantResponse as removeLastAssistantResponseAction,
  setCurrentConversationId as setCurrentConversationIdAction,
  clearActiveDocument as clearActiveDocumentAction,
  getConversation as getConversationAction,
  syncConversationFromServer as syncConversationFromServerAction,
  restoreContextAttachments as restoreContextAttachmentsAction,
  saveContextAttachments as saveContextAttachmentsAction,
  clearContextAttachments as clearContextAttachmentsAction,
  clearTemporaryAttachments as clearTemporaryAttachmentsAction,
  clearLastGeneratedImageUrl as clearLastGeneratedImageUrlAction,
  clearLastGeneratedArtifactId as clearLastGeneratedArtifactIdAction,
  getPromptsRemainingLimit as getPromptsRemainingLimitAction,
  storeGeneratedPodcast as storeGeneratedPodcastAction,
  removeGeneratedPodcast as removeGeneratedPodcastAction,
  markStreamStopRequested as markStreamStopRequestedAction,
} from '../actions/aiAssistant.actions';
import useAction from '../utils/useAction';
import { isRateLimited } from '../utils/isRateLimited';
import { v4 } from 'uuid';
import { ConversationNewMessage } from '../reducers/aiAssistant.reducers';
import {
  ConversationMessageContext,
  TemplatePrompts
} from '@kimuno/api-sdk/lib/types/common/Conversation';
import { AsyncActionStatus } from '../actions/common.actionTypes';
import { ActiveView } from '../components/AIAssistant/shared/constants';
import { FieldName } from '../components/AIAssistant/shared/TemplatePromptForm/formConfigs';
import { notifyBugsnagHandledError, notifyBugsnagNetwork } from '../utils/bugsnagClient';
import { GeneratedPodcast, GeneratedPodcastStageStatus } from '@kimuno/api-sdk/lib/types/responses/Ai';
import useMukiBudeOnboarding,
{ MukiBudeOnboardingState, MukiBudeOnboardingStep } from '../components/AIAssistant/Dialog/hooks/useMukiBudeOnboarding';
import { useReaderContext } from '../components/Reader/ReaderContext';
import { getAIAssistantPageSource } from '../utils/getAIAssistantPageSource';

export enum AIAssistantDisplayMode {
  DIALOG = 'DIALOG',
  PAGE = 'PAGE',
}

// Stable fallback so useSelector's reference equality holds when the slice is absent.
const EMPTY_PODCASTS_BY_ID: StoreType['aiAssistant']['conversations']['podcasts']['byId'] = {};

// Why a podcast detail open is in flight. The two entry paths start from different
// screens, so each consumer picks its own placeholder from this.
export enum PodcastDetailsPendingReason {
  // Arrived at KI-Tools from elsewhere (My Contents card click / deep link). The page
  // mounts cold — nothing on screen to preserve, so a full-page spinner covers the gap.
  PAGE_ENTRY = 'PAGE_ENTRY',
  // Already on the page watching generation; the poll just settled. The split shell is
  // on screen and must be held until the detail opens, or the layout collapses to
  // chat-only for a commit and reads as the whole view flickering.
  GENERATION_FINISHED = 'GENERATION_FINISHED',
}

interface AIAssistantContext {
  isAssistantDragging: boolean;
  setIsAssistantDragging: (isAssistantDragging: boolean) => void;
  isAssistantOpen: boolean;
  openAssistant: (mindItemId?: string, conversationId?: string) => Promise<void>;
  closeAssistant: VoidFunction;
  assistantPosition: {x: number, y: number};
  setAssistantPosition: (position: {x: number, y: number}) => void;
  draggableBounds: {left: number, right: number, top: number, bottom: number};
  setDraggableBounds: (bounds: {left: number, right: number, top: number, bottom: number}) => void;
  isExtended: boolean
  toggleExtended: VoidFunction,
  setIsExtended: (isExtended: boolean) => void,
  dialogContentRef: React.RefObject<HTMLDivElement> | null
  dialogFooterRef: React.RefObject<HTMLDivElement> | null
  lastUserMessageRef: React.RefObject<HTMLDivElement> | null
  dialogMessagesContainerRef: React.RefObject<HTMLDivElement> | null
  isUserPromptLimitReached: boolean,
  isPageLimitReached: boolean,
  isMessagesRateLimitActive: boolean,
  isAttachmentsRateLimitActive: boolean,
  isPromptLimitReached: boolean,
  promptLimitAlertVisible: boolean,
  setPromptLimitAlertVisible: (visible: boolean) => void,
  showPromptLimitAlert: () => void,
  attachmentUploadError: string | null,
  setAttachmentUploadError: (error: string | null) => void,
  attachmentInfoMessage: string | null,
  setAttachmentInfoMessage: (message: string | null) => void,
  imageUploadError: string | null,
  setImageUploadError: (error: string | null) => void,
  documentDownloadAlertVisible: boolean,
  setDocumentDownloadAlertVisible: (visible: boolean) => void,
  imageGenerationLimitReachedAlertVisible: boolean,
  setImageGenerationLimitReachedAlertVisible: (visible: boolean) => void,
  isArtifactGenerationOnboardingAlertVisible: boolean,
  setIsArtifactGenerationOnboardingAlertVisible: (visible: boolean) => void,
  isAssistantDisabled: boolean;
  wasFeedbackOpen: boolean,
  setWasFeedbackOpen: (payload: boolean) => void,
  isFeedbackDialogOpen: boolean,
  setIsFeedbackDialogOpen: (payload: boolean) => void,
  activeView: ActiveView
  setActiveView: (view: ActiveView) => void
  selectedTemplatePromptId: TemplatePrompts | null
  setSelectedTemplatePromptId: (id: TemplatePrompts | null) => void
  // Per-field values seeded from an allowlisted `/ki-tools` launch link (dashboard
  // Muki cards). Merged over the template form's defaults on mount; `null` otherwise.
  templateLaunchPrefill: Partial<Record<FieldName, string>> | null
  setTemplateLaunchPrefill: (prefill: Partial<Record<FieldName, string>> | null) => void
  // Set synchronously by the launch consumer (AIToolsPage) so the view-sync effect,
  // which runs after it on mount, doesn't reset the launch-opened form to the landing.
  templateLaunchInProgressRef: React.MutableRefObject<boolean>
  processNewConversation: ({
    message,
    messageContext,
    activeViewOverride,
  }: {
    message: string,
    messageContext?: ConversationMessageContext,
    activeViewOverride?: ActiveView,
  }) => void
  stopGeneration: () => Promise<void>,
  isStoppingResponse: boolean,
  continueConversation: ({
    message,
    messageContext
  }: { 
    message: string, 
    messageContext?: ConversationMessageContext 
  }) => void
  regenerateResponse: () => void,
  resetActiveView: () => void,
  onboardingState: MukiBudeOnboardingState | null,
  stagesShownInCurrentSession: Set<MukiBudeOnboardingStep>,
  concludeOnboardingStep: (step: MukiBudeOnboardingStep) => void
  currentSessionHints: Set<string>,
  addCurrentSessionHint: (hint: string) => void
  aiFileUploadAbortControllerRef: React.MutableRefObject<AbortController | null> | null,
  displayMode: AIAssistantDisplayMode,
  readyPodcast: GeneratedPodcast | null,
  clearReadyPodcast: () => void,
  generatingPodcast: { id: string; title: string } | null,
  pendingPodcastDetailsReason: PodcastDetailsPendingReason | null,
  // True while a param-less entry probes the podcast status; the page holds a
  // neutral spinner so neither the chat nor the loading screen flashes wrongly.
  isPodcastEntryStatusPending: boolean,
  clearPendingPodcastDetails: () => void,
  preparePodcastNavigation: (isGenerating: boolean, podcastId: string) => void,
  closePodcastLoadingView: () => void,
  pendingPodcastIdRef: React.MutableRefObject<string | null>,
  isPodcastV1UxEnabled: boolean,
  // Consumers that must not act on `isPodcastV1UxEnabled` before the variant resolves —
  // until then it reads `false`, which is indistinguishable from the control cohort.
  isPodcastV1UxReady: boolean,
}

export const aiAssistantContext = createContext<AIAssistantContext>({
  isAssistantDragging: false,
  setIsAssistantDragging: () => {},
  isAssistantOpen: false,
  openAssistant: () => Promise.resolve(),
  closeAssistant: () => {},
  assistantPosition: { x: 0, y: 0 },
  setAssistantPosition: () => {},
  draggableBounds: { left: 0, right: 0, top: 0, bottom: 0 },
  setDraggableBounds: () => {},
  isExtended: false,
  toggleExtended: () => false,
  setIsExtended: () => {},
  dialogContentRef: null,
  dialogFooterRef: null,
  lastUserMessageRef: null,
  dialogMessagesContainerRef: null,
  isUserPromptLimitReached: false,
  isPageLimitReached: false,
  isMessagesRateLimitActive: false,
  isAttachmentsRateLimitActive: false,
  isPromptLimitReached: false,
  promptLimitAlertVisible: false,
  setPromptLimitAlertVisible: () => {},
  showPromptLimitAlert: () => {},
  attachmentUploadError: null,
  setAttachmentUploadError: () => {},
  attachmentInfoMessage: null,
  setAttachmentInfoMessage: () => {},
  imageUploadError: null,
  setImageUploadError: () => {},
  documentDownloadAlertVisible: false,
  setDocumentDownloadAlertVisible: () => {},
  imageGenerationLimitReachedAlertVisible: false,
  setImageGenerationLimitReachedAlertVisible: () => {},
  isArtifactGenerationOnboardingAlertVisible: false,
  setIsArtifactGenerationOnboardingAlertVisible: () => {},
  isAssistantDisabled: false,
  wasFeedbackOpen: false,
  setWasFeedbackOpen: () => {},
  isFeedbackDialogOpen: false,
  setIsFeedbackDialogOpen: () => {},
  activeView: ActiveView.CHAT,
  setActiveView: () => {},
  selectedTemplatePromptId: null,
  setSelectedTemplatePromptId: () => {},
  templateLaunchPrefill: null,
  setTemplateLaunchPrefill: () => {},
  templateLaunchInProgressRef: { current: false },
  processNewConversation: () => {},
  stopGeneration: async () => {},
  isStoppingResponse: false,
  continueConversation: () => {},
  regenerateResponse: () => {},
  resetActiveView: () => {},
  onboardingState: null,
  concludeOnboardingStep: () => {},
  stagesShownInCurrentSession: new Set(),
  currentSessionHints: new Set(),
  addCurrentSessionHint: () => {},
  aiFileUploadAbortControllerRef: null,
  displayMode: AIAssistantDisplayMode.DIALOG,
  readyPodcast: null,
  clearReadyPodcast: () => {},
  generatingPodcast: null,
  pendingPodcastDetailsReason: null,
  isPodcastEntryStatusPending: false,
  clearPendingPodcastDetails: () => {},
  preparePodcastNavigation: () => {},
  closePodcastLoadingView: () => {},
  pendingPodcastIdRef: { current: null },
  isPodcastV1UxEnabled: false,
  isPodcastV1UxReady: false,
});

const AIAssistantProvider: React.FC = ({ children }) => {
  const user = useSelector(selectUser);
  const { hasSeen: hasSeenArtifactAlert, record: recordArtifactAlertSeen } =
    useTriggeredHint(onboardingHints.aiAssistant.artifactGenerationOnboardingAlertSeen);
  const { match: { location: { pathname, query }, params }, router } = useRouter();
  const store = useStore<StoreType>();

  const [isAssistantDragging, setIsAssistantDragging] = useState(false);
  const [isAssistantOpen, setIsAssistantOpen] = useState(false);
  const [assistantPosition, setAssistantPosition] = useState({ x: 0, y: 0 });
  const [draggableBounds, setDraggableBounds] = useState({ left: 0, right: 0, top: 0, bottom: 0 });
  const [isExtended, setIsExtended] = useState(false);
  const { mindItemId, currentPage, selectedPagesForAI } = useReaderContext();
  const { onboardingState, concludeOnboardingStep, stagesShownInCurrentSession } = useMukiBudeOnboarding();
  const [displayMode, setDisplayMode] = useState<AIAssistantDisplayMode>(AIAssistantDisplayMode.DIALOG);

  // Local refs
  const dialogContentRef = useRef<HTMLDivElement>(null);
  const dialogFooterRef = useRef<HTMLDivElement>(null);
  const lastUserMessageRef = useRef<HTMLDivElement>(null);
  const dialogMessagesContainerRef = useRef<HTMLDivElement>(null);
  const lastVisitedPathRef = useRef(pathname);
  const previousPathForConversationIdSyncRef = useRef(pathname);
  const abortControllerRef = useRef<AbortController | null>(null);
  const aiFileUploadAbortControllerRef = useRef<AbortController | null>(null);
  const previousContextKeyRef = useRef<string | null>(null);
  const isSendingMessageRef = useRef<boolean>(false);
  const isPodcastLoadingViewRef = useRef(false);
  // set only when the poll settles; blocks the intercept from bouncing back to the loading screen
  const podcastSettledConversationIdRef = useRef<string | null>(null);
  // True once a poll tick observed the podcast still generating. Separates a watched
  // generation (hand the finished detail over) from a conversation whose podcast was
  // already settled on arrival — entering via URL must land in plain chat.
  const podcastPollSawGeneratingRef = useRef(false);
  const [isPodcastPollDone, setIsPodcastPollDone] = useState(false);
  // The whole settled payload, not just its id: the poll already fetched it, so the
  // detail view can open from it instead of repeating the request.
  const [readyPodcast, setReadyPodcast] = useState<GeneratedPodcast | null>(null);
  // One-shot: consumed by the editor's handover open.
  const clearReadyPodcast = useCallback(() => setReadyPodcast(null), []);
  // Title the poll surfaces mid-generation (available before audio is ready), so the
  // loading top bar and chat card can replace their placeholders once it's known. The
  // loading view's own heading intentionally keeps its static placeholder throughout.
  const [generatingPodcast, setGeneratingPodcast] = useState<{ id: string; title: string } | null>(null);
  const [isPodcastEntryStatusPending, setIsPodcastEntryStatusPending] = useState(false);
  const [pendingPodcastDetailsReason, setPendingPodcastDetailsReason] =
    useState<PodcastDetailsPendingReason | null>(null);
  // Carries the clicked podcast id; cached conversation messages can predate it.
  const pendingPodcastIdRef = useRef<string | null>(null);
  const clearPendingPodcastDetails = useCallback(() => {
    setPendingPodcastDetailsReason(null);
    pendingPodcastIdRef.current = null;
  }, []);

  // Called by Meine Inhalte cards before navigating, so the AI Tools page
  // mounts straight into the right view instead of flashing the previous one.
  const preparePodcastNavigation = useCallback((isGenerating: boolean, podcastId: string) => {
    pendingPodcastIdRef.current = podcastId;
    if (isGenerating) {
      isPodcastLoadingViewRef.current = true;
      setActiveView(ActiveView.PODCAST_LOADING);
    } else {
      setPendingPodcastDetailsReason(PodcastDetailsPendingReason.PAGE_ENTRY);
      setActiveView(ActiveView.CHAT);
    }
  }, []);

  const abortCurrentStream = useCallback((reason: string) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort(reason);
      abortControllerRef.current = null;
    }
  }, []);

  // Amplitude experiments and session state
  const [wasFeedbackOpen, setWasFeedbackOpen] = useState(false);
  // Held here, not in the dialog component: each page mounts its own layout tree,
  // so component-local open state would not survive navigation.
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false);
  const [isAssistantDisabled, setIsAssistantDisabled] = useState(false);
  const [activeView, setActiveView] = useState<ActiveView>(ActiveView.FREE_DRAFT_ENTRY);
  const [selectedTemplatePromptId, setSelectedTemplatePromptId] = useState<TemplatePrompts | null>(null);
  const [templateLaunchPrefill, setTemplateLaunchPrefill] =
    useState<Partial<Record<FieldName, string>> | null>(null);
  // Mirrors activeView for the URL-sync effect below, which must not stomp a
  // launch-selected template form back to the landing when it re-runs on the
  // query-strip navigation. Assigned during render so it is current before effects.
  const activeViewRef = useRef(activeView);
  activeViewRef.current = activeView;
  const templateLaunchInProgressRef = useRef(false);
  // Tracks whether the previous render was on KI-Tools, so the leave-path resets
  // activeView/prefill exactly once on the transition away — not on every render
  // of an unrelated page (e.g. the reader), where activeView is switched legitimately.
  const wasOnAIToolsPageRef = useRef(false);

  const {
    isReady: isDisableFeatureReady,
    variant: disableFeatureVariant
  } = useAmplitudeExperiment(user, config.amplitudeExperiments.disableAiAssistantButton);

  // Resolved once here and shared via context so the router (AIToolsPage) and the
  // mounted chat shell (AIAssistantChat) gate on the SAME readiness/variant — a
  // per-component hook would let the freshly-mounted shell read a not-yet-ready
  // 'control' on the first PODCAST_LOADING render.
  const {
    isReady: isPodcastV1UxReady,
    variant: podcastV1UxVariant
  } = useAmplitudeExperiment(user, config.amplitudeExperiments.enablePodcastV1Ux);
  const isPodcastV1UxEnabled = isPodcastV1UxReady && podcastV1UxVariant === 'on';

  // Conversation state
  const currentConversationId = useSelector(getCurrentConversationId);
  const setCurrentConversationId = useAction(setCurrentConversationIdAction);

  // X on the loading view: back to the chat while generation continues —
  // the status-driven chat card and the background poll take over. Clears the watch
  // machinery so no GENERATION_FINISHED auto-open fires after a dismissed watch, and
  // strips podcast intent from the URL.
  const closePodcastLoadingView = useCallback(() => {
    isPodcastLoadingViewRef.current = false;
    podcastPollSawGeneratingRef.current = false;
    setIsPodcastPollDone(false);
    clearPendingPodcastDetails();
    // Mark the conversation as probed — otherwise the entry probe re-runs on the
    // CHAT flip, still sees a generating podcast, and re-opens the view just closed.
    podcastSettledConversationIdRef.current = currentConversationId;
    setActiveView(ActiveView.CHAT);
    if (query?.podcastId) {
      router.replace(pathname);
    }
  }, [clearPendingPodcastDetails, currentConversationId, query?.podcastId, pathname, router]);
  const currentConversation = useSelector(getCurrentConversation);
  // True from the Stop press until the stop settles; the footer shows a
  // disabled Stop button meanwhile (the stream stays open, see stopGeneration).
  // Cleared on the stream's LOADING → settled transition; the podcast-cancel
  // branch clears it itself when its request finishes.
  const [isStoppingResponse, setIsStoppingResponse] = useState(false);
  const prevConversationStatusRef = useRef(currentConversation?.status);
  useEffect(() => {
    if (
      prevConversationStatusRef.current === AsyncActionStatus.LOADING &&
      currentConversation?.status !== AsyncActionStatus.LOADING
    ) {
      setIsStoppingResponse(false);
    }
    prevConversationStatusRef.current = currentConversation?.status;
  }, [currentConversation?.status]);
  const generatedPodcastIdFromStream = useSelector(getGeneratedPodcastId);
  const generatedPodcastIdFromMessages = useMemo(() => {
    const messages = currentConversation?.data?.messages;
    if (!messages) return null;
    const podcastMessage = messages.find(
      (msg) => msg.role === 'assistant' && (msg.context as any)?.generatedPodcastId
    );
    return (podcastMessage?.context as any)?.generatedPodcastId as string | null ?? null;
  }, [currentConversation?.data?.messages]);
  const generatedPodcastId = generatedPodcastIdFromStream || generatedPodcastIdFromMessages;
  const createNewConversation = useAction(createNewConversationAction);
  const addNewMessage = useAction(addNewMessageAction);
  const saveContextAttachments = useAction(saveContextAttachmentsAction);
  const restoreContextAttachments = useAction(restoreContextAttachmentsAction);
  const clearContextAttachments = useAction(clearContextAttachmentsAction);
  const clearTemporaryAttachments = useAction(clearTemporaryAttachmentsAction);
  const temporaryAttachments = useSelector(getTemporaryAttachments);
  const streamConversationResponse = useAction(streamConversationResponseAction);
  const removeLastAssistantResponse = useAction(removeLastAssistantResponseAction);
  const clearActiveDocument = useAction(clearActiveDocumentAction);
  const getConversation = useAction(getConversationAction);
  const syncConversationFromServer = useAction(syncConversationFromServerAction);
  const storeGeneratedPodcast = useAction(storeGeneratedPodcastAction);
  const removeGeneratedPodcast = useAction(removeGeneratedPodcastAction);
  const markStreamStopRequested = useAction(markStreamStopRequestedAction);
  // const activeDocumentData = useSelector(getActiveDocumentData);
  const userPromptCount = currentConversation?.data?.messages.filter(({ role }) => role === 'user').length ?? 0;
  const isUserPromptLimitReached = userPromptCount >= config.ai.readerChat.maxUserPromptCount;
  const pageCount = currentConversation?.data?.context?.mindItem?.pages?.length ?? 0;
  const isPageLimitReached = pageCount >= config.ai.readerChat.maxDocumentPagesCount;
  const messagesRateLimitExpiresAt = currentConversation?.data?.rateLimit?.messages?.expiresAt ?? null;
  const attachmentsRateLimitExpiresAt = currentConversation?.data?.rateLimit?.attachments?.expiresAt ?? null;
  const isMessagesRateLimitActive = isRateLimited(messagesRateLimitExpiresAt);
  const isAttachmentsRateLimitActive = isRateLimited(attachmentsRateLimitExpiresAt);
  const promptsRemainingLimit = useSelector(getPromptsRemainingLimit);
  const isPromptLimitReached = promptsRemainingLimit === 0;
  const fetchPromptsRemainingLimit = useAction(getPromptsRemainingLimitAction);
  const [promptLimitAlertVisible, setPromptLimitAlertVisible] = useState(false);
  const prevPromptsRemainingLimitRef = useRef(promptsRemainingLimit);
  const showPromptLimitAlert = useCallback(() => {
    if (isPromptLimitReached) {
      setPromptLimitAlertVisible(true);
    }
  }, [isPromptLimitReached]);

  // Show alert only when remaining transitions from >0 to 0 (prompt #100)
  useEffect(() => {
    const prev = prevPromptsRemainingLimitRef.current;
    prevPromptsRemainingLimitRef.current = promptsRemainingLimit;
    if (prev !== null && prev > 0 && promptsRemainingLimit === 0) {
      setPromptLimitAlertVisible(true);
    } else if (prev === 0 && promptsRemainingLimit !== null && promptsRemainingLimit > 0) {
      setPromptLimitAlertVisible(false);
    }
  }, [promptsRemainingLimit]);
  const availableAgainAt = useSelector(getPromptsAvailableAgainAt);
  useEffect(() => {
    if (!availableAgainAt) return;
    const delay = availableAgainAt - Date.now();
    if (delay <= 0) {
      fetchPromptsRemainingLimit();
      return;
    }
    const timer = setTimeout(() => fetchPromptsRemainingLimit(), delay);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableAgainAt]);

  const [attachmentUploadError, setAttachmentUploadError] = useState<string | null>(null);
  const [attachmentInfoMessage, setAttachmentInfoMessage] = useState<string | null>(null);
  const [imageUploadError, setImageUploadError] = useState<string | null>(null);
  const [documentDownloadAlertVisible, setDocumentDownloadAlertVisible] = useState(false);
  const [imageGenerationLimitReachedAlertVisible, setImageGenerationLimitReachedAlertVisible] =
    useState(false);
  const [isArtifactGenerationOnboardingAlertVisible, setIsArtifactGenerationOnboardingAlertVisible] = useState(false);
  const clearLastGeneratedImageUrl = useAction(clearLastGeneratedImageUrlAction);
  const clearLastGeneratedArtifactId = useAction(clearLastGeneratedArtifactIdAction);
  const [currentSessionHints, setCurrentSessionHints] = useState<Set<string>>(new Set());
  const prevDisplayModeRef = useRef<AIAssistantDisplayMode>(displayMode);
  
  const isReaderPage = pathname.startsWith(`/${routes.reader.root}/`);

  useEffect(() => {
    fetchPromptsRemainingLimit();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isReaderPage) {
      setDisplayMode(AIAssistantDisplayMode.DIALOG);
    } else {
      setDisplayMode(AIAssistantDisplayMode.PAGE);
    }
  }, [pathname]);

  // Clean up conversation state when switching between display modes
  useEffect(() => {
    const prevMode = prevDisplayModeRef.current;
    const currentMode = displayMode;

    if (prevMode !== currentMode) {
      const isAIToolsPage = pathname.startsWith(`/${routes.aiTools.root}`);
      const urlConversationId = (params as { conversationId?: string })?.conversationId;
      const shouldPreserveConversation = isAIToolsPage && !!urlConversationId;

      // Only clear if assistant is closed to avoid disrupting active sessions
      if (!isAssistantOpen && !shouldPreserveConversation) {
        setCurrentConversationId({ conversationId: null });
      }
    }

    prevDisplayModeRef.current = currentMode;
  }, [displayMode, isAssistantOpen, pathname, params, setCurrentConversationId]);

  // Get conversations data for memoization
  const conversationsById = useSelector((state: StoreType) => state.aiAssistant.conversations.byId);
  const temporaryConversation = useSelector(getTemporaryConversation);
  
  // Memoized function to find conversation by mindItemId
  const findConversationForMindItem = useCallback((mindItemId: string | undefined): string | null => {
    if (!mindItemId) return null;
    
    // Check temporary conversation first
    if (temporaryConversation?.data?.context?.mindItem?.id === mindItemId) {
      return temporaryConversation.data.id;
    }
    
    // Search through conversations
    for (const [conversationId, conversationState] of Object.entries(conversationsById)) {
      const conversation = conversationState?.data;
      if (conversation?.context?.mindItem?.id === mindItemId) {
        return conversationId;
      }
    }
    
    return null;
  }, [conversationsById, temporaryConversation]);
  
  const openAssistant: AIAssistantContext['openAssistant'] = async (mindItemId?: string, conversationId?: string) => {
    setIsAssistantOpen(true);
    const mindItem = mindItemId ? getMindItem(store.getState(), mindItemId)?.data : undefined;
    const isViewingDocumentMindItem = mindItem?.fileType === 'document';
    services.track.ampliTrackAIAssistantOpenedEvent({ mindItem, pageSource: getAIAssistantPageSource(pathname) });
    if (conversationId) {
      try {
        await getConversation({ conversationId });
        setCurrentConversationId({ conversationId });
        setActiveView(ActiveView.CHAT);
      } catch (error) {
        notifyBugsnagNetwork('AIAssistant', 'openAssistant', { conversationId, mindItemId, error });
      }
      return;
    }
    
    if (isReaderPage && mindItemId) {
      const existingConversationId = findConversationForMindItem(mindItemId);
      
      if (existingConversationId) {
        const currentState = store.getState();
        const conversationExists = currentState.aiAssistant.conversations.byId[existingConversationId] ||
          currentState.aiAssistant.conversations.temporaryConversation?.data?.id === existingConversationId;
        
        if (conversationExists) {
          setCurrentConversationId({ conversationId: existingConversationId });
          setActiveView(ActiveView.CHAT);
        } else {
          setActiveView(ActiveView.UNIT_ENTRY);
        }
      } else {
        setActiveView(ActiveView.UNIT_ENTRY);
      }
    } else if (isViewingDocumentMindItem) {
      setActiveView(ActiveView.UNIT_ENTRY);
    } else {
      setActiveView(ActiveView.FREE_DRAFT_ENTRY);
    }
  };

  const closeAssistant = () => {
    setIsAssistantOpen(false);
    setCurrentConversationId({ conversationId: null });
    clearActiveDocument();
  };

  useEffect(() => {
    const isAIToolsPage = pathname.startsWith(`/${routes.aiTools.root}`);
    if (!isAIToolsPage) {
      clearPendingPodcastDetails();
      // AIAssistantProvider is app-wide, so activeView + a prior launch's prefill
      // persist across routes. Reset them once when leaving KI-Tools so a return
      // starts on the fresh landing instead of a stale (or re-seeded) template form.
      if (wasOnAIToolsPageRef.current) {
        wasOnAIToolsPageRef.current = false;
        setTemplateLaunchPrefill(null);
        // Only clear a stale template form. Do NOT touch CHAT: navigating from Bude
        // to the reader ("Open Unit") calls openAssistant → CHAT, and this leave-reset
        // would otherwise race it and strip the conversation view back to the landing.
        // The return-to-KI-Tools path already lands on FREE_DRAFT_ENTRY when there is
        // no conversation, so the fresh-landing intent is preserved.
        if (activeViewRef.current === ActiveView.TEMPLATE_PROMPT_FORM) {
          setActiveView(ActiveView.FREE_DRAFT_ENTRY);
        }
        // A stale loading view would make the next conversation entry skip the
        // CHAT flip and re-enter the loading screen uninvited.
        if (activeViewRef.current === ActiveView.PODCAST_LOADING) {
          isPodcastLoadingViewRef.current = false;
          setActiveView(ActiveView.FREE_DRAFT_ENTRY);
        }
      }
      return;
    }
    wasOnAIToolsPageRef.current = true;

    // Get conversationId from URL params (authoritative source for AI Tools page)
    const urlConversationId = (params as { conversationId?: string })?.conversationId;
    const previousPathname = previousPathForConversationIdSyncRef.current;
    const isUserNavigatingFromOtherPage = pathname !== previousPathname;

    // If the URL has a conversation ID
    // and it differs from Redux then update Redux conversation ID from URL.
    if (urlConversationId) {
      if (urlConversationId !== currentConversationId) {
        setCurrentConversationId({ conversationId: urlConversationId });
        getConversation({ conversationId: urlConversationId });
      }
    }
    // If the URL **does not** have a conversation ID
    // and Redux has a conversation ID
    else if (currentConversationId) {
      if (isUserNavigatingFromOtherPage) {
        // and the user is navigating from other page, then clear the Redux conversation ID.
        setCurrentConversationId({ conversationId: null });
        // A cleared conversation cannot be loading a podcast.
        isPodcastLoadingViewRef.current = false;
      } else {
        // If user is not navigating from other page, push the Redux conversation ID to the URL.
        router.replace(`/${routes.aiTools.root}/${currentConversationId}`);
      }
    }

    previousPathForConversationIdSyncRef.current = pathname;

    // Skip view switching while podcast is generating — the transition
    // effect handles moving to CHAT once the conversation is ready.
    if (isPodcastLoadingViewRef.current === true) {
      return;
    } else if (urlConversationId || currentConversationId) {
      setActiveView(ActiveView.CHAT);
    } else if (
      activeViewRef.current === ActiveView.TEMPLATE_PROMPT_FORM ||
      templateLaunchInProgressRef.current
    ) {
      // A launch opened the template form with no conversation. On the initial mount
      // the launch consumer's effect runs first and flips the ref before this effect;
      // on a later query-strip re-run activeView has already committed to the form.
      // Either way, don't reset it back to the landing.
      templateLaunchInProgressRef.current = false;
    } else {
      setActiveView(ActiveView.FREE_DRAFT_ENTRY);
    }
  }, [currentConversationId, pathname, params, router, clearPendingPodcastDetails]);

  const resetActiveView = () => {
    setActiveView(ActiveView.FREE_DRAFT_ENTRY);
  };

  const processNewConversation: AIAssistantContext['processNewConversation'] = async ({
    message, messageContext, activeViewOverride,
  }) => {
    setReadyPodcast(null);
    setGeneratingPodcast(null);
    const id = v4();
    const newMessage: ConversationNewMessage = { 
      id,
      role: 'user',
      content: [{ text: message, type: 'text' }],
      context: messageContext
    };

    const context = mindItemId ? {
      mindItem: {
        id: mindItemId,
        pages: selectedPagesForAI.length > 0 ? selectedPagesForAI : [currentPage - 1]
      }
    } : {};
    
    isSendingMessageRef.current = true;
    
    const currentContextKey = `${currentConversationId || 'new'}_${activeView}`;
    clearContextAttachments({ contextKey: currentContextKey });
    clearTemporaryAttachments();

    createNewConversation({
      id,
      message: newMessage,
      context
    });

    isPodcastLoadingViewRef.current = activeViewOverride === ActiveView.PODCAST_LOADING;
    setActiveView(activeViewOverride || ActiveView.CHAT);
    abortCurrentStream(ABORT_REASON.USER_SWITCHED_ACTIVE_VIEW);
    abortControllerRef.current = new AbortController();
    streamConversationResponse({
      conversationId: id,
      abortController: abortControllerRef.current,
    });
  };

  const continueConversation: AIAssistantContext['continueConversation']  = ({ message, messageContext }) => {
    if (!currentConversationId) {
      return;
    }

    isSendingMessageRef.current = true;
    
    const currentContextKey = `${currentConversationId || 'new'}_${activeView}`;
    clearContextAttachments({ contextKey: currentContextKey });
    clearTemporaryAttachments();

    addNewMessage({
      conversationId: currentConversationId,
      message: {
        id: v4(),
        role: 'user',
        content: [{ text: message, type: 'text' }],
        context: messageContext
      }
    });

    abortCurrentStream(ABORT_REASON.USER_SWITCHED_ACTIVE_VIEW);
    abortControllerRef.current = new AbortController();
    streamConversationResponse({
      conversationId: currentConversationId,
      abortController: abortControllerRef.current,
    });
  };

  const regenerateResponse = () => {
    if (!currentConversationId) {
      return;
    }

    removeLastAssistantResponse({
      conversationId: currentConversationId
    });

    abortCurrentStream(ABORT_REASON.USER_SWITCHED_ACTIVE_VIEW);
    abortControllerRef.current = new AbortController();
    streamConversationResponse({
      conversationId: currentConversationId,
      abortController: abortControllerRef.current,
      regenerate: true,
    });
  };

  // Cancel an already-created podcast via the stop endpoint: the server aborts
  // the job, deletes the record + audio, and rewrites the assistant message.
  // A 409 (finished in the same instant) or 404 (already cancelled elsewhere)
  // proceeds to the same cleanup; the re-sync then shows the server's truth.
  const cancelPodcastGeneration = async (
    conversationId: string,
    podcastId: string,
  ) => {
    try {
      try {
        await services.ai.stopConversationGeneration(conversationId, podcastId);
      } catch (error) {
        const statusCode = (error as { statusCode?: number })?.statusCode;
        if (statusCode !== 409 && statusCode !== 404) {
          // Cancel failed (e.g. network): keep the generating state so the
          // poll and the Stop button stay truthful.
          notifyBugsnagHandledError(error as Error);
          return;
        }
      }

      removeGeneratedPodcast({ podcastId });
      setGeneratingPodcast(null);
      if (activeViewRef.current === ActiveView.PODCAST_LOADING) {
        closePodcastLoadingView();
      }

      const conversation = await services.conversation.getConversation(conversationId);
      syncConversationFromServer({ conversationId, conversation });
    } catch (syncError) {
      notifyBugsnagHandledError(syncError as Error);
    } finally {
      setIsStoppingResponse(false);
    }
  };

  // The Stop button's single entry point. Sends the server whatever ids exist:
  // a known podcast id (from the stream event or the store) cancels that
  // podcast; otherwise the running turn is stopped (Mongo intent + same-
  // instance registry + server-side sweep). The fetch is never aborted: the
  // open stream delivers the settled outcome, so a bare disconnect
  // (navigate-away) never cancels anything.
  const stopGeneration = async () => {
    // Read fresh from the store: for a brand-new conversation the server-
    // assigned id arrives via the stream metadata event moments after the
    // send, and a render-time closure could still hold the old values.
    const state = store.getState();
    const conversationId = state.aiAssistant.conversations.currentConversationId;
    const podcastId =
      state.aiAssistant.conversations.stream.generatedPodcastId ||
      getGeneratingPodcastIdForCurrentConversation(state);

    // Before the metadata event there is no id to address: a click is a no-op
    // (the window lasts milliseconds; the next click goes through).
    if (!conversationId) {
      return;
    }

    setIsStoppingResponse(true);
    if (getCurrentConversation(state)?.status === AsyncActionStatus.LOADING) {
      markStreamStopRequested();
    }

    if (podcastId) {
      await cancelPodcastGeneration(conversationId, podcastId);
      return;
    }

    try {
      await services.ai.stopConversationGeneration(conversationId);
    } catch (error) {
      notifyBugsnagHandledError(error as Error);
    }
    // The stopping state clears when the stream settles (status effect above).
  };

  const toggleExtended = () => {
    setIsExtended(!isExtended);
  };

  useEffect(() => {
    const isReaderPage = pathname.startsWith(`/${routes.reader.root}/`);
    const prevPath = lastVisitedPathRef.current;
    const prevIsReaderPage = prevPath.startsWith(`/${routes.reader.root}/`);

    // Close only when navigating away from the reader to avoid closing on page->reader transitions.
    if (pathname !== prevPath && prevIsReaderPage && !isReaderPage && isAssistantOpen) {
      closeAssistant();
    }

    if (!isReaderPage) {
      if (activeView === ActiveView.UNIT_ENTRY) {
        setActiveView(ActiveView.FREE_DRAFT_ENTRY);
      }
    }

    lastVisitedPathRef.current = pathname;
  }, [pathname, activeView, isAssistantOpen]);

  // Opening a conversation with a generated podcast must never flash a wrong view:
  // a podcastId URL carries explicit intent, so it lands on the loading screen right
  // away while it resolves; everything else holds the neutral entry spinner until a
  // status probe decides between the chat (settled) and the loading screen (still
  // generating, whose poll then routes ready/errored to CHAT).
  useEffect(() => {
    // The loading view only renders on the KI Tools page; never route to it elsewhere.
    if (!pathname.startsWith(`/${routes.aiTools.root}`)) return;
    if (activeView !== ActiveView.CHAT) return;
    if (pendingPodcastDetailsReason) return;
    if (!currentConversationId || currentConversation?.status !== AsyncActionStatus.READY) return;
    if (podcastSettledConversationIdRef.current === currentConversationId) return;

    // An explicit document or image URL wins over the podcast landing screen. Mark the
    // conversation settled, not just bail: both query params get stripped later (imageId
    // after the scroll, edit on editor close), and a bare return would re-arm the
    // landing the moment they disappear.
    if ((query?.edit === 'true' && query?.id) || query?.imageId) {
      podcastSettledConversationIdRef.current = currentConversationId;
      return;
    }

    const messages = currentConversation?.data?.messages ?? [];
    const podcastId = (messages.find(
      (msg) => msg.role === 'assistant' && (msg.context as any)?.generatedPodcastId
    )?.context as any)?.generatedPodcastId as string | undefined;
    if (!podcastId) return;

    if (query?.podcastId) {
      isPodcastLoadingViewRef.current = true;
      setActiveView(ActiveView.PODCAST_LOADING);
      return;
    }

    let cancelled = false;
    setIsPodcastEntryStatusPending(true);

    services.ai.getGeneratedPodcast(podcastId)
      .then((podcast) => {
        if (cancelled) return;
        const isSettled = podcast.status === GeneratedPodcastStageStatus.READY
          || podcast.status === GeneratedPodcastStageStatus.ERROR;
        if (isSettled) {
          podcastSettledConversationIdRef.current = currentConversationId;
        } else {
          isPodcastLoadingViewRef.current = true;
          setActiveView(ActiveView.PODCAST_LOADING);
        }
      })
      .catch((error) => {
        if (cancelled) return;
        // Fail open to the chat — the loading screen must only show when proven true.
        podcastSettledConversationIdRef.current = currentConversationId;
        notifyBugsnagHandledError(error as Error);
      })
      .finally(() => {
        if (!cancelled) setIsPodcastEntryStatusPending(false);
      });

    return () => {
      cancelled = true;
      setIsPodcastEntryStatusPending(false);
    };
  }, [activeView, currentConversationId, currentConversation?.status, pendingPodcastDetailsReason, pathname,
    query?.edit, query?.id, query?.imageId, query?.podcastId]);

  // Transition from loading screen to chat once the generated podcast poll
  // confirms the podcast is ready (or errored).
  // Also clear the ref if activeView was changed away from PODCAST_LOADING by another code path
  // (e.g. openAssistant, resetActiveView) so the URL sync effect can resume driving activeView.
  useEffect(() => {
    if (activeView === ActiveView.PODCAST_LOADING) {
      if (isPodcastPollDone) {
        podcastSettledConversationIdRef.current = currentConversationId;
        isPodcastLoadingViewRef.current = false;
        setIsPodcastPollDone(false);
        // Hand over to the detail view only after a watched generation; a podcast that
        // was already settled on arrival stays in plain chat.
        if (podcastPollSawGeneratingRef.current) {
          podcastPollSawGeneratingRef.current = false;
          // Armed in the same batch as the view flip: leaving CHAT and the open detail
          // to separate commits is what let the chat-only layout paint in between.
          setPendingPodcastDetailsReason(PodcastDetailsPendingReason.GENERATION_FINISHED);
        }
        setActiveView(ActiveView.CHAT);
      }
    } else {
      isPodcastLoadingViewRef.current = false;
    }
  }, [activeView, isPodcastPollDone, currentConversationId]);

  // Fetch the conversation from the API to discover the generatedPodcastId,
  // then poll getGeneratedPodcast every 5s until it returns ready or error.
  const PODCAST_POLL_INTERVAL = 5000;
  useEffect(() => {
    if (
      // Poll only while the loading view is actually visible, which requires the KI Tools page.
      !pathname.startsWith(`/${routes.aiTools.root}`) ||
      activeView !== ActiveView.PODCAST_LOADING ||
      !currentConversationId ||
      currentConversation?.status !== AsyncActionStatus.READY
    ) return;

    let intervalId: ReturnType<typeof setInterval> | null = null;
    let cancelled = false;

    const start = async () => {
      let podcastId = pendingPodcastIdRef.current || generatedPodcastId;

      if (!podcastId) {
        try {
          const conversation = await services.conversation.getConversation(currentConversationId);
          if (cancelled) return; // effect cleaned up mid-fetch — don't touch the store
          // Sync the store so the assistant message carries generatedPodcastId; the local
          // stream never learns it, and play/pause tracking keys off that message context.
          syncConversationFromServer({ conversationId: currentConversationId, conversation });
          const podcastMessage = conversation.messages.find(
            (msg) => msg.role === 'assistant' && (msg.context as any)?.generatedPodcastId
          );
          podcastId = (podcastMessage?.context as any)?.generatedPodcastId ?? null;
        } catch (error) {
          notifyBugsnagHandledError(error as Error);
          // Settle the poll so the user isn't stranded on the loading screen.
          if (!cancelled) setIsPodcastPollDone(true);
          return;
        }
      }

      if (cancelled) return; // stale run — must not settle the poll for a newer one
      if (!podcastId) {
        // The server conversation has no podcast message either — settle instead
        // of leaving the loading screen up with nothing to poll.
        setIsPodcastPollDone(true);
        return;
      }

      const poll = async () => {
        try {
          // Ask for the script up front so the settled payload is everything the detail
          // view needs — it opens from this instead of re-fetching mid-handover.
          const podcast = await services.ai.getGeneratedPodcast(podcastId!, { includeScriptText: true });
          // Stale response — the effect cleaned up (conversation change / unmount)
          // while this was in flight; skip every state update for it.
          if (cancelled) return;

          // Persist every tick: the chat card's generating state reads the store, and
          // without a LOADING entry it flickers to "settled" the moment the view closes.
          storeGeneratedPodcast({ podcast });

          // The title is populated before the audio is ready, so capture it every
          // tick to feed the loading top bar and chat card.
          if (podcast.title) {
            setGeneratingPodcast({ id: podcastId!, title: podcast.title });
          }

          if (
            podcast.status === GeneratedPodcastStageStatus.READY ||
            podcast.status === GeneratedPodcastStageStatus.ERROR
          ) {
            if (intervalId) clearInterval(intervalId);
            // A watched generation just finished: re-sync the completion message the
            // assistant API appends on success (flag-gated) and stash the settled
            // payload so the handover opens the detail from it. A podcast already
            // settled on the first tick skips both — the conversation load carried
            // the completion message, and no detail view opens.
            if (podcastPollSawGeneratingRef.current) {
              if (podcast.status === GeneratedPodcastStageStatus.READY && isPodcastV1UxEnabled) {
                try {
                  const conversation = await services.conversation.getConversation(currentConversationId);
                  if (cancelled) return; // effect cleaned up mid-fetch — don't touch the store
                  syncConversationFromServer({ conversationId: currentConversationId, conversation });
                } catch (syncError) {
                  notifyBugsnagHandledError(syncError as Error);
                }
              }
              setReadyPodcast(podcast);
            }
            setIsPodcastPollDone(true);
          } else {
            podcastPollSawGeneratingRef.current = true;
          }
        } catch (error) {
          // 404: the podcast was cancelled (record deleted), e.g. from another
          // tab. Settle the poll so the user is not stranded on the loading view.
          if ((error as { statusCode?: number })?.statusCode === 404) {
            if (cancelled) return;
            if (intervalId) clearInterval(intervalId);
            removeGeneratedPodcast({ podcastId: podcastId! });
            setIsPodcastPollDone(true);
            return;
          }
          notifyBugsnagHandledError(error as Error);
        }
      };

      poll();
      intervalId = setInterval(poll, PODCAST_POLL_INTERVAL);
    };

    start();

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [
    activeView,
    currentConversationId,
    generatedPodcastId,
    currentConversation?.status,
    pathname,
    isPodcastV1UxEnabled,
  ]);

  // A podcast generated mid-chat shows no loading screen, so poll every
  // still-generating podcast in the background: the card flips to ready and the
  // server's completion message arrives without a refresh. Keyed on a joined id
  // string so the effect restarts only when the set of generating podcasts changes,
  // and paused while the creation flow's PODCAST_LOADING poll owns the view.
  const podcastsById = useSelector(
    (state: StoreType) => state.aiAssistant.conversations.podcasts?.byId ?? EMPTY_PODCASTS_BY_ID
  );
  const generatingPodcastIdsKey = useMemo(() => {
    const messages = currentConversation?.data?.messages ?? [];
    const ids = messages
      .filter((msg) => msg.role === 'assistant' && (msg.context as any)?.generatedPodcastId)
      .map((msg) => (msg.context as any).generatedPodcastId as string)
      .filter((id) => podcastsById[id]?.data?.status === GeneratedPodcastStageStatus.LOADING);
    return [...new Set(ids)].join(',');
  }, [currentConversation?.data?.messages, podcastsById]);

  useEffect(() => {
    if (
      !isPodcastV1UxEnabled ||
      !pathname.startsWith(`/${routes.aiTools.root}`) ||
      activeView === ActiveView.PODCAST_LOADING ||
      !currentConversationId ||
      currentConversation?.status !== AsyncActionStatus.READY ||
      !generatingPodcastIdsKey
    ) return;

    const podcastIds = generatingPodcastIdsKey.split(',');
    let cancelled = false;

    const poll = async () => {
      for (const podcastId of podcastIds) {
        try {
          const podcast = await services.ai.getGeneratedPodcast(podcastId, { includeScriptText: true });
          if (cancelled) return;
          if (podcast.status === GeneratedPodcastStageStatus.LOADING) continue;

          // Settled: store it (the card flips out of its generating state) and re-sync
          // the completion message the assistant API appends on success. No detail view
          // opens — the user stays on whatever they have open.
          if (podcast.status === GeneratedPodcastStageStatus.READY) {
            try {
              const conversation = await services.conversation.getConversation(currentConversationId);
              if (cancelled) return;
              syncConversationFromServer({ conversationId: currentConversationId, conversation });
            } catch (syncError) {
              notifyBugsnagHandledError(syncError as Error);
            }
          }
          storeGeneratedPodcast({ podcast });
        } catch (error) {
          // 404: cancelled (record deleted): drop it from the store, which
          // empties the generating-ids key and ends this poll.
          if ((error as { statusCode?: number })?.statusCode === 404) {
            if (cancelled) return;
            removeGeneratedPodcast({ podcastId });
            continue;
          }
          notifyBugsnagHandledError(error as Error);
        }
      }
    };

    poll();
    const intervalId = setInterval(poll, PODCAST_POLL_INTERVAL);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [
    isPodcastV1UxEnabled,
    pathname,
    activeView,
    currentConversationId,
    currentConversation?.status,
    generatingPodcastIdsKey,
    storeGeneratedPodcast,
    syncConversationFromServer,
  ]);

  useEffect(() => {
    const isAssistantDisabled = isDisableFeatureReady && disableFeatureVariant === 'on';
    setIsAssistantDisabled(isAssistantDisabled);
  }, [isDisableFeatureReady, disableFeatureVariant]);

  // Clear document stream and transient states when conversation changes
  const prevConversationIdRef = useRef(currentConversationId);
  
  useEffect(() => {
    const conversationChanged = prevConversationIdRef.current !== currentConversationId;
    
    if (conversationChanged) {
      // readyPodcast is only meaningful for the conversation that was polling
      setReadyPodcast(null);
      setGeneratingPodcast(null);
      setIsStoppingResponse(false);
      podcastPollSawGeneratingRef.current = false;
      setSelectedTemplatePromptId(null);
      setTemplateLaunchPrefill(null);
      setAttachmentUploadError(null);
      setAttachmentInfoMessage(null);
      setImageUploadError(null);
      setDocumentDownloadAlertVisible(false);
      setImageGenerationLimitReachedAlertVisible(false);
      setIsArtifactGenerationOnboardingAlertVisible(false);
      clearLastGeneratedImageUrl();
    }
    prevConversationIdRef.current = currentConversationId;
  }, [currentConversationId, clearLastGeneratedImageUrl]);

  const imagesRemainingLimit = useSelector(getAiGeneratedImagesRemainingLimit);
  const lastGeneratedImageUrl = useSelector(getLastGeneratedImageUrl);
  const lastGeneratedArtifactId = useSelector(getLastGeneratedArtifactId);

  // Show the limit-reached alert when a generation just completed and the 7-day
  // image quota is now exhausted. lastGeneratedImageUrl (set together with
  // remainingLimit on TOOL_USE_END) is the "just generated this session" signal;
  // consuming it here prevents re-triggering and keeps history/on-mount loads silent.
  useEffect(() => {
    if (lastGeneratedImageUrl && imagesRemainingLimit === 0) {
      setImageGenerationLimitReachedAlertVisible(true);
      clearLastGeneratedImageUrl();
    }
  }, [lastGeneratedImageUrl, imagesRemainingLimit, clearLastGeneratedImageUrl]);

  useEffect(() => {
    if (!lastGeneratedArtifactId) return;
    // Consume the signal before the page check so an artifact generated
    // elsewhere (e.g. Reader) cannot trigger the celebration later on KI Tools
    clearLastGeneratedArtifactId();

    if (!pathname.startsWith(`/${routes.aiTools.root}`)) return;
    if (hasSeenArtifactAlert) return;

    recordArtifactAlertSeen();
    setIsArtifactGenerationOnboardingAlertVisible(true);
  }, [lastGeneratedArtifactId, pathname, clearLastGeneratedArtifactId, hasSeenArtifactAlert, recordArtifactAlertSeen]);

  useEffect(() => {
    const getContextKey = (convId: string | null, view: ActiveView) => {
      return `${convId || 'new'}_${view}`;
    };

    const currentContextKey = getContextKey(currentConversationId, activeView);
    const previousContextKey = previousContextKeyRef.current;
    const wasSendingMessage = isSendingMessageRef.current;
    
    isSendingMessageRef.current = false;

    if (previousContextKey === null) {
      restoreContextAttachments({ contextKey: currentContextKey });
      previousContextKeyRef.current = currentContextKey;
      return;
    }

    if (previousContextKey !== currentContextKey) {
      if (!wasSendingMessage) {
        saveContextAttachments({
          contextKey: previousContextKey,
          attachments: temporaryAttachments,
        });
      }
      
      restoreContextAttachments({ contextKey: currentContextKey });
      previousContextKeyRef.current = currentContextKey;
    }
  }, [activeView, currentConversationId]);

  return ( 
    <aiAssistantContext.Provider
      value={{
        isAssistantDragging,
        setIsAssistantDragging,
        isAssistantDisabled,
        isAssistantOpen,
        openAssistant,
        closeAssistant,
        assistantPosition,
        setAssistantPosition,
        draggableBounds,
        setDraggableBounds,
        isExtended,
        toggleExtended,
        setIsExtended,
        dialogContentRef,
        dialogFooterRef,
        lastUserMessageRef,
        dialogMessagesContainerRef,
        isUserPromptLimitReached,
        isPageLimitReached,
        isMessagesRateLimitActive,
        isAttachmentsRateLimitActive,
        isPromptLimitReached,
        promptLimitAlertVisible,
        setPromptLimitAlertVisible,
        showPromptLimitAlert,
        attachmentUploadError,
        setAttachmentUploadError,
        attachmentInfoMessage,
        setAttachmentInfoMessage,
        imageUploadError,
        setImageUploadError,
        documentDownloadAlertVisible,
        setDocumentDownloadAlertVisible,
        imageGenerationLimitReachedAlertVisible,
        setImageGenerationLimitReachedAlertVisible,
        isArtifactGenerationOnboardingAlertVisible,
        setIsArtifactGenerationOnboardingAlertVisible,
        wasFeedbackOpen,
        setWasFeedbackOpen,
        isFeedbackDialogOpen,
        setIsFeedbackDialogOpen,
        activeView,
        setActiveView,
        selectedTemplatePromptId,
        setSelectedTemplatePromptId,
        templateLaunchPrefill,
        setTemplateLaunchPrefill,
        templateLaunchInProgressRef,
        processNewConversation,
        stopGeneration,
        isStoppingResponse,
        continueConversation,
        regenerateResponse,
        resetActiveView,
        onboardingState,
        concludeOnboardingStep,
        currentSessionHints,
        addCurrentSessionHint: (hint: string) => setCurrentSessionHints(prev => new Set(prev).add(hint)),
        stagesShownInCurrentSession,
        aiFileUploadAbortControllerRef,
        displayMode,
        readyPodcast,
        clearReadyPodcast,
        generatingPodcast,
        pendingPodcastDetailsReason,
        isPodcastEntryStatusPending,
        clearPendingPodcastDetails,
        preparePodcastNavigation,
        closePodcastLoadingView,
        pendingPodcastIdRef,
        isPodcastV1UxEnabled,
        isPodcastV1UxReady,
      }}
    >
      {children}
    </aiAssistantContext.Provider>
  );
};

export default AIAssistantProvider;
